/**
 * Code 128 (set B) as SVG - the barcode on a slip
 *
 * The scanner at the counter and on the terminal reads it and types out the code (`CODE-CONTROL`). Set B covers the printable
 * ASCII characters (32-126), which is enough for a ticket code, a dash and a control code. Without a library:
 * the table is standard and unchangeable, and a package would be a dependency of the shell for this alone.
 *
 * Every character is six widths (a bar, a space, a bar, ...) which add up to 11 modules; the stop has seven
 * and 13 modules. The check character is `(104 + sum of position x value) mod 103`.
 */
export const PATTERNS = [
    '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
    '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
    '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
    '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
    '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
    '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
    '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
    '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
    '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
    '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
    '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
];
const START_B = 104;
const STOP = 106;

/**
 * The values of the characters, with the start and the check one, without the stop
 *
 * @param text what goes into the code
 * @returns the code values, start and checksum included
 */
export const values = (text: string): number[] =>
{
    const codes = [...String(text)].map((char) =>
    {
        const code = char.charCodeAt(0);

        if (code < 32 || code > 126)
        {
            throw new Error(`code128: znak "${char}" nije u skupu B`);
        }

        return code - 32;
    });

    const checksum = codes.reduce((sum, value, index) => sum + (value * (index + 1)), START_B) % 103;

    return [START_B, ...codes, checksum];
};

/**
 * the widths of every bar and space, from the start to the stop
 *
 * @param text what goes into the code
 * @returns one width per bar and space, in module units
 */
export const widths = (text: string): number[] => [...values(text), STOP]
    .map((value) => PATTERNS[value])
    .join('')
    .split('')
    .map(Number);

/**
 * @param text what goes into the code
 * @param options `module` is the width of the narrowest bar in px, `height` the height in px
 * @returns the SVG
 */
export const svg = (text: string, { module = 2, height = 56 }: { module?: number; height?: number } = {}): string =>
{
    const quiet = 10 * module;

    const bars: string[] = [];

    let x = quiet;

    widths(text).forEach((width, index) =>
    {
        // even ones are bars, odd ones spaces
        if (index % 2 === 0)
        {
            bars.push(`<rect x="${x}" y="0" width="${width * module}" height="${height}"/>`);
        }

        x += width * module;
    });

    const total = x + quiet;

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${total}" height="${height}" `
        + `viewBox="0 0 ${total} ${height}" shape-rendering="crispEdges"><rect width="${total}" `
        + `height="${height}" fill="#fff"/><g fill="#000">${bars.join('')}</g></svg>`;
};

export default {
    PATTERNS, values, widths, svg,
};
